import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import PatientSearch from "../../../Components/PatientSearch";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import moment from "moment";
import AddAppointment from "../Reception/Appointments2/addAppointment/AddAppointment";
import TicketPrint from "./TicketPrint";

export default function TicketCreate() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    patient: null,
    department: null,
    visit_date: moment().format("YYYY-MM-DDTHH:mm"),
    notes: "",
    status: 1, // Active
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [createdTicket, setCreatedTicket] = useState(null);
  const [quickActionOpen, setQuickActionOpen] = useState(false);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [printTicketOpen, setPrintTicketOpen] = useState(false);

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/departments/`,
          {
            headers: { Authorization: `Token ${token}` },
          }
        );
        setDepartments(response.data?.results || response.data || []);
      } catch (err) {
        console.error("Error fetching departments:", err);
      }
    };
    fetchDepartments();
  }, [token]);

  const createTicket = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/tickets/`,
        data,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["tickets"]);
      setCreatedTicket(data);
      setPrintTicketOpen(true);
    },
    onError: (err) => {
      setError(err.response?.data?.detail || "Failed to create ticket");
    },
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!form.patient) {
      setError("Please select a patient");
      setLoading(false);
      return;
    }

    const submitData = {
      patient: form.patient.id || form.patient,
      department: form.department?.id || form.department || null,
      visit_date: form.visit_date,
      notes: form.notes || "",
      status: form.status,
    };

    try {
      await createTicket.mutateAsync(submitData);
    } catch (err) {
      // Error handled in mutation
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
          Create New Ticket
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={onSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <PatientSearch
                value={form.patient}
                onChange={(v) => setForm((f) => ({ ...f, patient: v }))}
                label="Patient *"
                placeholder="Search patient by name or phone..."
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Department"
                name="department"
                value={form.department?.id || form.department || ""}
                onChange={(e) => {
                  const dept = departments.find((d) => d.id === parseInt(e.target.value));
                  setForm((f) => ({ ...f, department: dept || null }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Visit Date & Time"
                name="visit_date"
                value={form.visit_date}
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={form.status}
                onChange={onChange}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={2}>Completed</MenuItem>
                <MenuItem value={3}>Cancelled</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                name="notes"
                value={form.notes}
                onChange={onChange}
                placeholder="Additional notes about this visit..."
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate("/reception/ticketing/tickets")}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                >
                  Create Ticket
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Quick Action Dialog */}
      <Dialog
        open={quickActionOpen}
        onClose={() => setQuickActionOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          {t("ticketing.ticketCreated", { defaultValue: "Ticket Created Successfully!" })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
            {t("ticketing.whatWouldYouLikeToDo", { defaultValue: "What would you like to do next?" })}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={() => {
                setQuickActionOpen(false);
                setAppointmentDialogOpen(true);
              }}
              sx={{ py: 1.5 }}
            >
              {t("reception.createOPD", { defaultValue: "Create OPD Appointment" })}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              fullWidth
              onClick={() => {
                setQuickActionOpen(false);
                if (createdTicket) {
                  navigate(`/reception/ticketing/tickets/${createdTicket.id}`);
                } else {
                  navigate("/reception/ticketing/tickets");
                }
              }}
              sx={{ py: 1.5 }}
            >
      {t("ticketing.viewTicket", { defaultValue: "View Ticket" })}
            </Button>
            <Button
              variant="text"
              color="inherit"
              size="medium"
              fullWidth
              onClick={() => {
                setQuickActionOpen(false);
                navigate("/reception/ticketing/tickets");
              }}
              sx={{ py: 1 }}
            >
              {t("common.close", { defaultValue: "Close" })}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Auto Print Dialog */}
      <Dialog
        open={printTicketOpen}
        onClose={() => setPrintTicketOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          {createdTicket && (
            <TicketPrint ticket={createdTicket} onClose={() => setPrintTicketOpen(false)} />
          )}
        </DialogContent>
      </Dialog>

      {/* Appointment Dialog */}
      {createdTicket && (
        <AddAppointment
          open={appointmentDialogOpen}
          onClose={() => {
            setAppointmentDialogOpen(false);
            navigate("/reception/ticketing/tickets");
          }}
          initialTicket={createdTicket}
        />
      )}
    </Box>
  );
}
